import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { FadeUp, SectionHead } from "../Reveal";
import { SITE } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  {
    icon: Phone,
    label: "Telefon",
    phones: SITE.phones,
    hint: "Mobilde dokunun, hemen arayın",
    testid: "contact-info-phone",
  },
  {
    icon: Mail,
    label: "E-posta",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    hint: "Tıklayın, mail uygulaması açılır",
    testid: "contact-info-email",
  },
  {
    icon: MapPin,
    label: "Adres",
    value: SITE.address,
    href: SITE.mapsUrl,
    hint: "Google Maps'te aç",
    testid: "contact-info-address",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      setError("Gönderim sırasında bir sorun oluştu. Lütfen tekrar deneyin veya bizi arayın.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", phone: "", email: "", message: "" });
    setSent(false);
  };

  return (
    <section id="iletisim" className="scroll-mt-24 bg-paper-deep py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <SectionHead
          eyebrow="İletişim"
          title="Bizimle İletişime Geçin"
          desc="Tarım makinelerimiz hakkında detaylı bilgi almak için bizimle iletişime geçebilirsiniz."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-5 lg:col-span-2">
            {INFO.map((item, i) => (
              <FadeUp key={item.label} delay={0.06 * i}>
                {item.phones ? (
                  <div className="card flex items-start gap-4 p-5" data-testid={item.testid}>
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border border-gold/30 bg-gold/15 text-gold">
                      <item.icon size={19} />
                    </span>
                    <span>
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-soil">
                        {item.label}
                      </span>
                      {item.phones.map((p, idx) => (
                        <a
                          key={p.href}
                          href={p.href}
                          data-testid={`contact-phone-${idx}`}
                          className="mt-1 block text-sm font-bold text-ink transition-colors duration-200 hover:text-gold sm:text-base"
                        >
                          {p.display}
                        </a>
                      ))}
                      <span className="mt-1 block text-xs text-soil">{item.hint}</span>
                    </span>
                  </div>
                ) : (
                  <a
                    href={item.href}
                    target={item.label === "Adres" ? "_blank" : undefined}
                    rel={item.label === "Adres" ? "noopener noreferrer" : undefined}
                    data-testid={item.testid}
                    className="card group flex items-start gap-4 p-5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-ink/40"
                  >
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border border-gold/30 bg-gold/15 text-gold">
                      <item.icon size={19} />
                    </span>
                    <span>
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-soil">
                        {item.label}
                      </span>
                      <span className="mt-1 block text-sm font-bold text-ink sm:text-base">
                        {item.value}
                      </span>
                      <span className="mt-1 block text-xs text-soil transition-colors duration-200 group-hover:text-gold">
                        {item.hint}
                      </span>
                    </span>
                  </a>
                )}
              </FadeUp>
            ))}
            <FadeUp delay={0.2}>
              <p className="inline-flex items-center gap-2 px-1 font-mono text-[11px] uppercase tracking-[0.18em] text-soil">
                <Clock size={14} className="text-gold" />
                {SITE.hours}
              </p>
            </FadeUp>
            <FadeUp delay={0.26}>
              <div className="overflow-hidden rounded-lg border border-line">
                <iframe
                  title="Baytaş Tarım konum haritası"
                  src={SITE.mapsEmbed}
                  loading="lazy"
                  className="h-64 w-full"
                  data-testid="contact-map"
                />
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.1} className="lg:col-span-3">
            <div className="card p-6 sm:p-8">
              {sent ? (
                <div className="flex flex-col items-start py-6" data-testid="contact-form-success">
                  <span className="grid h-12 w-12 place-items-center rounded-full border border-gold/30 bg-gold/15 text-gold">
                    <CheckCircle2 size={24} />
                  </span>
                  <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-ink">
                    Mesajınız İletildi
                  </h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-soil">
                    Teşekkürler! Mesajınız başarıyla gönderildi. Ekibimiz en kısa sürede size dönüş
                    yapacak.
                  </p>
                  <button type="button" onClick={reset} data-testid="contact-form-reset" className="btn btn-ghost mt-6">
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="label">Ad Soyad</label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        value={form.name}
                        onChange={set("name")}
                        data-testid="contact-form-name"
                        placeholder="Adınız Soyadınız"
                        className="input"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="label">Telefon</label>
                      <input
                        id="contact-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        data-testid="contact-form-phone"
                        placeholder="05XX XXX XX XX"
                        className="input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-email" className="label">E-posta</label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        data-testid="contact-form-email"
                        placeholder="ornek@eposta.com"
                        className="input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className="label">Mesaj</label>
                      <textarea
                        id="contact-message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        data-testid="contact-form-message"
                        placeholder="İlgilendiğiniz makine ve sorularınızı yazın..."
                        className="input resize-y"
                      />
                    </div>
                  </div>

                  {error && (
                    <p
                      role="alert"
                      data-testid="contact-form-error"
                      className="mt-4 rounded-md border border-ember/30 bg-ember/10 px-4 py-3 text-sm font-semibold text-ember"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-form-submit"
                    className="btn btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    <Send size={16} />
                    {sending ? "Gönderiliyor..." : "Gönder"}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}